<script lang="ts">
  import { onMount, tick } from "svelte"
  import { xhr, toast, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import {
    ListVideo,
    ListPlus,
    Pencil,
    Trash,
    Save,
    CircleX
  } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    playlists: string[]
    onLoadPlaylist: (playlist: string) => void
  }

  let props = $props()
  let { onLoadPlaylist }: Props = props
  let playlists = $state(props.playlists)

  let currentPlaylist: PlaylistType = $state(null)
  let errors = $state({})
  let playlistTitle = $state("")
  let formStatus = $state("")
  let newPlaylistTitleEl = $state(null)
  let newPlaylistEl = $state(null)

  async function addPlaylist(): Promise<void> {
    errors = {}
    playlistTitle = ""
    formStatus = "new"
    await tick()
    scrollIntoView(newPlaylistTitleEl)
    newPlaylistTitleEl.focus()
  }

  async function createPlaylist() {
    errors = {}

    try {
      const resp = await xhr.post(`/plans/${appStore.plan.id}/playlists`, {
        playlist: { title: playlistTitle }
      })

      const playlist = resp.data
      playlists = [...playlists, playlist]

      formStatus = ""
    } catch (e) {
      toast("There was an error creating the playlist", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    }
  }

  let editTitleEl = $state(null)
  async function editPlaylist(playlist: PlaylistType): Promise<void> {
    errors = {}
    formStatus = "edit"
    playlistTitle = playlist.title
    currentPlaylist = playlist
    await tick()
    editTitleEl.focus()
  }

  async function updatePlaylist() {
    errors = {}
    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/playlists/${currentPlaylist.id}`,
        {
          playlist: { title: playlistTitle }
        }
      )

      let idx = playlists.findIndex((b) => b.id === resp.data.id)

      playlists[idx].title = resp.data.title
      formStatus = ""
    } catch (e) {
      toast("There was an error updating the playlist", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    }
  }

  async function deletePlaylist(playlist: PlaylistType) {
    playlist._selected = true
    playlist = playlist
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected playlist?")) {
      playlist._selected = false
      playlist = playlist
      return
    }

    try {
      await xhr.delete(`/plans/${appStore.plan.id}/playlists/${playlist.id}`)

      let idx = playlists.findIndex((b) => b.id === playlist.id)
      playlists.splice(idx, 1)
      playlists = playlists
      toast("Playlist was successfully deleted", "success")
    } catch (e) {
      toast("There was an error deleting the playlist", "error")
      console.error(e)
    }
  }

  const scrollIntoView = (node) => {
    node.scrollIntoView({
      behavior: "smooth"
    })
  }
</script>

<div>
  <h3 class="text-lg font-bold mb-2 flex items-center">
    <ListVideo class="w-5 h-5 text-gray-600 mr-1" />
    Playlists
  </h3>
  <ul class="w-full bg-gray-50 rounded border border-gray-100 py-4 px-3">
    {#each playlists as playlist}
      <li
        class="flex justify-start items-center py-2 border-b border-gray-200"
        class:bg-red-200={playlist._selected === true}
      >
        {#if formStatus === "edit" && currentPlaylist.id === playlist.id}
          <div class="w-full bg-white rounded border-gray-200 border p-2">
            <div class="w-full flex flex-nowrap items-center justify-between">
              <input
                class="block w-full py-2 text-lg rounded-md border-gray-300 px-3 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                bind:value={playlistTitle}
                bind:this={editTitleEl}
              />
            </div>
            <div class="mt-1 flex flex-nowrap justify-end">
              <button
                class="btn primary text-sm p-1"
                onclick={() => updatePlaylist()}
              >
                <Save class="size-5" />
              </button>
              <button
                class="ml-2 btn secondary text-sm p-1"
                onclick={() => (formStatus = "")}
              >
                <CircleX class="size-5" />
              </button>
            </div>
            <div class="text-red-500 mt-2 text-sm text-center">
              {errors.title || ""}
            </div>
          </div>
        {:else}
          <button
            class="text-base text-left text-black px-4 grow hover:text-blue-600 hover:underline"
            onclick={() => onLoadPlaylist(playlist)}
          >
            {playlist.title}
          </button>
          <button onclick={() => editPlaylist(playlist)}>
            <Pencil class="size-5 text-gray-600 hover:text-blue-600" /></button
          >
          <button onclick={() => deletePlaylist(playlist)} class="ml-3">
            <Trash class="size-5 text-gray-600 hover:text-blue-600" /></button
          >
        {/if}
      </li>
    {:else}
      <li>No Playlists</li>
    {/each}
  </ul>

  {#if formStatus === "new"}
    <div
      class="mt-4 p-2 bg-blue-50 border border-blue-100 rounded"
      bind:this={newPlaylistEl}
    >
      <div class="w-full flex items-center justify-between">
        <input
          type="text"
          class="block w-full py-2 text-lg rounded-md border-gray-300 px-3 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          bind:this={newPlaylistTitleEl}
          bind:value={playlistTitle}
          placeholder="Playlist title"
        />
      </div>

      <div class="mt-2 w-full flex justify-end">
        <button class="btn primary" onclick={() => createPlaylist()}>
          Create
        </button>
        <button class="ml-2 btn secondary" onclick={() => (formStatus = "")}>
          Cancel
        </button>
      </div>

      <div class="text-red-500 mt-2 text-sm text-center">
        {errors.title || ""}
      </div>
    </div>
  {/if}

  <button
    onclick={addPlaylist}
    class="w-full mt-2 flex justify-center items-center rounded border py-2 px-4 border-blue-200 bg-blue-50 text-sm hover:bg-blue-100"
  >
    <ListPlus class="w-4 h-4 text-gray-600 mr-1" />
    Add Playlist</button
  >
</div>
